/**
 * Colour ramps for figures: a part is given one base colour and its shade and highlight are derived
 * the way a pixel artist picks them — shadows lean cool and richer, lights lean warm and softer —
 * so a look can be written with base colours only and every figure's ramps stay consistent.
 */
import type { Look } from './sprites';

type HSL = [number, number, number];

export function toHsl(hex: string): HSL {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}

export function fromHsl([h, s, l]: HSL): string {
  h = ((h % 360) + 360) % 360;
  s = Math.min(1, Math.max(0, s));
  l = Math.min(1, Math.max(0, l));
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return `#${[f(0), f(8), f(4)]
    .map((x) =>
      Math.round(x * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/**
 * The same colour in shadow (`dl` < 0) or light (`dl` > 0): shadows lean cool and more saturated,
 * lights lean warm, the way the game's hand-picked shades do.
 */
export function shade(hex: string, dl: number): string {
  const [h, s, l] = toHsl(hex);
  const towards = dl < 0 ? 240 : 50;
  const dh = ((towards - h + 540) % 360) - 180;
  return fromHsl([h + dh * Math.min(0.12, Math.abs(dl) * 0.4), s + (dl < 0 ? 0.06 : -0.04), l + dl]);
}

/**
 * Skin in shadow as a pixel artist picks it: a little rosier, softer and darker. Darkening skin at
 * its own saturation (`shade`) turns a pale skin orange, which reads as a stain rather than a shadow.
 */
export function skinShade(hex: string): string {
  const [h, s, l] = toHsl(hex);
  return fromHsl([h - 8, s * 0.72, l * 0.83]);
}

/** Base colour → the companions derived from it, with their lightness change. */
export const TONES = {
  skin: [['skinS', -0.1]],
  hair: [['hairH', 0.18]],
  crown: [['crownS', -0.16]],
  outer: [
    ['outerS', -0.13],
    ['outerH', 0.16],
  ],
  inner: [['innerS', -0.14]],
  sash: [['sashS', -0.16]],
  boots: [['bootsS', 0.12]],
  blade: [['bladeS', -0.32]],
  cape: [['capeS', -0.12]],
} as const satisfies Record<string, readonly (readonly [keyof Look, number])[]>;

type Base = keyof typeof TONES;
type Derived = (typeof TONES)[Base][number][0];

/**
 * A look written with base colours: every companion shade and highlight left out is derived from
 * its base. Companions given explicitly are kept, so a hand-picked shade still wins.
 */
export function tones<T extends Partial<Look>>(look: T): T & Partial<Pick<Look, Derived>> {
  const out: Partial<Look> = { ...look };
  for (const [base, companions] of Object.entries(TONES) as [Base, (typeof TONES)[Base]][]) {
    const c = look[base];
    if (typeof c !== 'string') continue;
    for (const [k, dl] of companions)
      if (look[k] === undefined) (out as Record<string, string>)[k] = shade(c, dl);
  }
  return out as T & Partial<Pick<Look, Derived>>;
}
